import { Observable, of } from 'rxjs';

import { Container, Service } from '../../../../symphony';

import { SlDiscussContext } from '../../global-constants';
import { SlEventsService } from '../Business/sl-events.service';
import { SlQuestionService } from '../Business/sl-question.service';
import { IActionData } from '../../global-interface';
import { SlVotingService } from '../Business/sl-voting.service';
import { SlRepliesService } from '../Business/sl-replies.service';
import { SlTrackingService } from '../Business/sl-tracking.service';
import { IDiscussVoteResponseData, IDiscussPostData, IDiscussRepliesResponseData } from '../../../../api/public/discuss-api';

@Service()
export class SlActionsController {
	private eventsService = Container.take(SlDiscussContext, SlEventsService);

	private questionService = Container.take(
		SlDiscussContext,
		SlQuestionService,
	);

	private repliesService = Container.take(SlDiscussContext, SlRepliesService);

	private votingService = Container.take(SlDiscussContext, SlVotingService);

	private trackingService = Container.take(
		SlDiscussContext,
		SlTrackingService,
	);

	// Mount events
	public onDiscussQuestionMountHandler = ({
		questionId,
		questionAlias,
	}: IActionData): Observable<null> => this.eventsService.onDiscussQuestionMount(
		questionId,
		questionAlias,
	);

	public onDiscussQuestionUnMountHandler = (): Observable<null> => this.eventsService.onDiscussQuestionUnMount();

	public onDiscussRepliesMountHandler = ({
		questionId,
		currentPageNumber,
		sortType,
	}: IActionData): Observable<null> => this.eventsService.onDiscussRepliesMount(
		questionId,
		currentPageNumber,
		sortType,
	);

	public onDiscussRepliesUnMountHandler = (): Observable<null> => this.eventsService.onDiscussRepliesUnMount();

	public onHotTodayMountHandler = (): Observable<null> => this.eventsService.onHotTodayMount();

	public onDiscussQuestionsListPageMountHandler = (): Observable<null> => this.eventsService.onDiscussQuestionsListPageMount();

	public onCreateEditQuestionMountHandler = ({
		questionPageType,
		questionId,
	}: IActionData): Observable<null> => this.eventsService.onCreateEditQuestionMount(
		questionPageType,
		questionId,
	);

	public onCreateEditQuestionUnMountHandler = (): Observable<null> => this.eventsService.onCreateEditQuestionUnMount();

	public onPostQuestionModalMountHandler = (): Observable<null> => this.eventsService.onPostQuestionModalMount();

	public onDiscussQuestionsListMountHandler = ({
		questionsListFilter,
		questionsListSearchText,
		currentPageNumber,
	}: IActionData): Observable<null> => this.eventsService.onDiscussQuestionsListMount(
		questionsListSearchText,
		questionsListFilter,
		currentPageNumber,
	);

	// Click events
	public onPositiveReactionClickHandler = ({
		postId,
	}: IActionData): Observable<null> => this.eventsService.onPositiveReactionClick(postId);

	public onReplySaveClickHandler = ({
		replyContent,
		id,
	}: IActionData): Observable<null> => this.eventsService.onReplySaveClick(replyContent, id);

	public onAnswerButtonClickHandler = (): Observable<null> => this.eventsService.onAnswerButtonClick();

	public onCreateReplyCancelClickHandler = (): Observable<null> => this.eventsService.onCreateReplyCancelClick();

	public onCloseSaveReplySuccessMessageHandler = (): Observable<null> => this.eventsService.onCloseSaveReplySuccessMessage();

	public onBestAnswerButtonClickHandler = ({
		replyId,
	}: IActionData): Observable<null> => this.eventsService.onBestAnswerButtonClick(replyId);

	public onProfileButtonClickHandler = ({
		userId,
	}: IActionData): Observable<null> => this.eventsService.onProfileButtonClick(userId);

	public onQuestionClickHandler = ({ id }: IActionData): Observable<null> => this.eventsService.onQuestionClick(id);

	public onTopicClickHandler = ({
		id,
		pageAlias,
	}: IActionData): Observable<null> => this.eventsService.onTopicClick(id, pageAlias);

	public onTagClickHandler = ({
		alias,
		questionId,
	}: IActionData): Observable<null> => this.eventsService.onTagClick(alias, questionId);

	public onSearchClickHandler = ({
		searchedInputValue,
	}: IActionData): Observable<null> => this.eventsService.onSearchClick(searchedInputValue);

	public onAskAQuestionClickHandler = (): Observable<null> => this.eventsService.onAskAQuestionClick();

	public onPaginationClickHandler = ({
		currentPageNumber,
		paginationCtaType,
		clickedPageNumber,
	}: IActionData): Observable<null> => this.eventsService.onPaginationClick(
		currentPageNumber,
		paginationCtaType,
		clickedPageNumber,
	);

	public onEmptyQuestionCTAClickHandler = ({
		ctaType,
		ctaURL,
	}: IActionData): Observable<null> => this.eventsService.onEmptyQuestionCTAClick(ctaType, ctaURL);

	public onQuestionSubmitHandler = ({
		postQuestionPayload,
		questionPageType,
	}: IActionData): Observable<null> => this.eventsService.onQuestionSubmit(
		postQuestionPayload,
		questionPageType,
	);

	public onQuestionPostCancelClickHandler = ({
		questionPageType,
	}: IActionData): Observable<null> => this.eventsService.onQuestionPostCancelClick(questionPageType);

	public onQuestionDeleteClickHandler = (): Observable<null> => this.eventsService.onQuestionDeleteClick();

	public onPostQuestionModalCloseClickHandler = (): Observable<null> => this.eventsService.onPostQuestionSuccessCloseClick();

	public onSeeAllCoursesClickHandler = (): Observable<null> => this.eventsService.onSeeAllCoursesClick();

	public onRecommendedCourseClickHandler = ({
		alias,
	}: IActionData): Observable<null> => this.eventsService.onRecommendedCourseClick(alias);

	public onQuestionDeleteModalCloseClickHandler = (): Observable<null> => this.eventsService.onQuestionDeleteModalCloseClick();

	public onQuestionDeleteModalDeleteClickHandler = (): Observable<null> => this.eventsService.onQuestionDeleteModalDeleteClick();

	public onReplyDeleteModalCloseClickHandler = (): Observable<null> => this.eventsService.onReplyDeleteModalCloseClick();

	public onReplyDeleteModalDeleteClickHandler = (): Observable<null> => this.eventsService.onReplyDeleteModalDeleteClick();

	public onQuestionEditClickHandler = (): Observable<null> => this.eventsService.onQuestionEditClick();

	public onJumpLinkClickHandler = ({
		linkClickSource,
		text,
		ctaURL,
	}: IActionData): Observable<null> => this.eventsService.onJumpLinkClick({
		linkClickSource,
		text,
		ctaURL,
	});

	public onReplyEditButtonClickHandler = ({
		id,
		text,
	}: IActionData): Observable<null> => this.repliesService.onReplyEditButtonClick(id, text);

	public onDeleteReplyClickHandler = ({
		id,
	}: IActionData): Observable<null> => this.eventsService.onReplyDeleteClick(id);

	// Input events
	public onPostTagInputChangeHandler = ({
		tagInput,
	}: IActionData): Observable<null> => this.eventsService.onPostTagInputChange(tagInput);

	// Business events
	public updatePositiveReactionHandler = ({
		postId,
	}: IActionData): Observable<IDiscussVoteResponseData> => this.votingService.updatePositiveReaction(postId);

	public getDiscussQuestionHandler = ({
		questionId,
		questionPageType,
	}: IActionData): Observable<IDiscussPostData> => this.questionService.getDiscussQuestion(
		questionId,
		questionPageType,
	);

	public getDiscussRepliesHandler = ({
		questionId,
		currentPageNumber,
		sortType,
	}: IActionData): Observable<IDiscussRepliesResponseData> => this.repliesService.getReplies(
		questionId,
		currentPageNumber,
		sortType,
	);

	public createReplyHandler = ({ replyContent }: IActionData) => this.repliesService.createReply(replyContent);

	public EditReplyHandler = ({ replyContent, id }: IActionData) => this.repliesService.editReply(replyContent, id);

	public triggerReplyFormVisibilityHandler = ({
		isReplyFormOpen,
	}: IActionData) => this.repliesService.triggerReplyFormVisibility(isReplyFormOpen);

	public triggerCreateReplySuccessMessageVisibilityHandler = ({
		isCreateReplySuccessMessageVisible,
	}: IActionData) => this.repliesService.triggerCreateReplySuccessMessageVisibility(
		isCreateReplySuccessMessageVisible,
	);

	public triggerEditReplySuccessMessageVisibilityHandler = ({
		isEditReplySuccessMessageVisible,
	}: IActionData) => this.repliesService.triggerEditReplySuccessMessageVisibility(
		isEditReplySuccessMessageVisible,
	);

	public getHotTodayTopicsHandler = () => this.questionService.getHotTodayTopics();

	public getQuestionsListHandler = ({
		questionsListFilter,
		questionsListSearchText,
		currentPageNumber,
	}: IActionData) => this.questionService.getQuestionsList(
		questionsListSearchText,
		questionsListFilter,
		currentPageNumber,
	);

	public toggleAcceptedAnswerHandler = ({ replyId }: IActionData) => this.repliesService.toggleAcceptedAnswer(replyId);

	public triggerNotificationVisibilityHandler = () => this.questionService.triggerNotificationVisibility();

	public questionFilterChangeHandler = ({ selectedFilter }: IActionData) => this.eventsService.questionFilterChange(selectedFilter);

	public postQuestionHandler = ({ postQuestionPayload }: IActionData) => this.questionService.postQuestion(postQuestionPayload);

	public editQuestionHandler = ({ postQuestionPayload }: IActionData) => this.questionService.editQuestion(postQuestionPayload);

	public deleteQuestionHandler = () => this.questionService.deleteQuestion();

	public deleteReplyHandler = () => this.repliesService.deleteReply();

	public getTagsHandler = ({ tagInput }: IActionData) => this.questionService.getTags(tagInput);

	// Tracking
	public onTrackActionHandler = ({
		trackData,
	}: IActionData): Observable<null> => {
		this.trackingService[trackData.actionName](trackData);
		return of(null);
	};
}
